import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { LocationWizardData } from '../types';

interface LocationAdminSettingsStepProps {
  data: LocationWizardData;
  updateData: (partial: Partial<LocationWizardData>) => void;
}

interface AdminYesNoFieldProps {
  /** Stable id base used to wire the label to the radio group for a11y. */
  idBase: string;
  label: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

/** A labelled Yes/No radio group with an ADMIN badge, used in the admin-only step. */
const AdminYesNoField = (props: AdminYesNoFieldProps) => {
  const { idBase, label, value, onChange } = props;
  return (
    <FormControl sx={{ display: 'block', mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <FormLabel id={`${idBase}-label`} sx={{ m: 0 }}>
          {label}
        </FormLabel>
        <Chip label="ADMIN" color="primary" size="small" sx={{ fontWeight: 700, height: 20 }} />
      </Box>
      <RadioGroup
        row
        aria-labelledby={`${idBase}-label`}
        value={value ? 'yes' : 'no'}
        onChange={(e) => onChange(e.target.value === 'yes')}
      >
        <FormControlLabel value="no" control={<Radio />} label="No" />
        <FormControlLabel value="yes" control={<Radio />} label="Yes" />
      </RadioGroup>
    </FormControl>
  );
};

/**
 * Step 6 (Admin Settings) of the Create/Edit Location wizard.
 *
 * Only rendered for SecurSpace admins. Houses the admin-only controls that were
 * previously part of Step 5 (Pricing): Charge Overages, Location Live, and QR
 * Scanner Available. Toggling "Location Live" here is the only way to change a
 * location's published status — intermediate saves never touch `live`.
 */
const LocationAdminSettingsStep = (props: LocationAdminSettingsStepProps) => {
  const { data, updateData } = props;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Admin Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        SecurSpace admin-only controls. These are not visible to supplier users.
      </Typography>

      <AdminYesNoField
        idBase="charge-overages"
        label="Charge Overages?"
        value={data.chargeOverages}
        onChange={(value) => updateData({ chargeOverages: value })}
      />

      <AdminYesNoField
        idBase="location-live"
        label="Location Live?"
        value={data.live}
        onChange={(value) => updateData({ live: value })}
      />

      <AdminYesNoField
        idBase="qr-scanner"
        label="QR Scanner Available?"
        value={data.qrScannerAvailable}
        onChange={(value) => updateData({ qrScannerAvailable: value })}
      />
    </Box>
  );
};

export default LocationAdminSettingsStep;

